import { ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { useEffect, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import {
    DvfField,
    dvfExtent,
    DvfPlaneLattice,
    dvfMaxMagnitude,
    dvfPatientToVoxel,
    dvfSampleNearest,
} from '~/domain/dvf/dvfField';
import { paneImageOf } from '~/engine/view/paneImage';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import type { PointMembership } from '~/domain/geometry/contourStackMembership';
import { BodyMaskBasis } from '~/domain/patient/bodyOutline';
import {
    clampDvfDisplayRange,
    DvfDisplayRangeMm,
} from '~/domain/dvf/dvfDisplayRange';
import {
    clearPlaneCanvas,
    PLANE_OVERLAY_CANVAS_STYLE,
    PLANE_OVERLAY_REDRAW_EVENTS,
    planeLattice,
} from './planeCanvas';
import {
    drawEdges,
    drawGlyphs,
    arrowDrawnLengthMm,
    arrowLengthScale,
    isOutsideDisplayRange,
    latticeEdgeIndices,
    magnitudeBucket,
    MIN_DISPLACEMENT_MM,
    ProjectedEdge,
    ProjectedGlyph,
} from '~/components/dvf/dvfGlyphs';

interface DvfOverlayProps {
    view: PlaneView | null;
    field: DvfField | undefined;
    arrows: boolean;
    grid: boolean;
    arrowSpacingMm: number;
    gridSpacingMm: number;
    rangeLowMm?: number;
    rangeHighMm?: number;
    insideBody?: PointMembership;
    basis: BodyMaskBasis;
    bounds?: Extent;
}

function displacementAt(dvf: DvfField, point: ReadonlyVec3): vec3 | undefined {
    const voxel = dvfPatientToVoxel(dvf, point);
    if (!voxel) {
        return undefined;
    }
    const disp = dvfSampleNearest(dvf, voxel[0], voxel[1], voxel[2]);
    return vec3.negate(disp, disp);
}

type LatticeMembership = (index: number) => boolean;

type Extent = { min: ReadonlyVec3; max: ReadonlyVec3 };

interface LatticeEntry {
    readonly view: PlaneView;
    readonly field: DvfField;
    readonly extent: Extent;
    readonly insideBody: PointMembership | undefined;
    readonly basis: BodyMaskBasis;
    readonly lattice: DvfPlaneLattice;
    inBody?: LatticeMembership | null;
}

// Only the scan's body is drawn: nothing while it is still being segmented, all without one.
function latticeBodyMask(
    lattice: DvfPlaneLattice,
    insideBody: PointMembership | undefined,
    basis: BodyMaskBasis
): LatticeMembership | null {
    if (insideBody) {
        return (index) => insideBody(lattice.points[index]);
    }
    return basis === 'computing' ? () => false : null;
}

function computeGlyphs(
    view: PlaneView,
    dvf: DvfField,
    lattice: DvfPlaneLattice,
    range: DvfDisplayRangeMm,
    lengthScale: number,
    spacingMm: number,
    inBody: LatticeMembership | null
): ProjectedGlyph[] {
    const out: ProjectedGlyph[] = [];
    const tip = vec3.create();
    for (let index = 0; index < lattice.points.length; index++) {
        if (inBody && !inBody(index)) {
            continue;
        }
        const base = lattice.points[index];
        const disp = displacementAt(dvf, base);
        if (!disp) {
            continue;
        }
        const mag = vec3.length(disp);
        const bucket = magnitudeBucket(mag, range);
        const isOutsideRange = isOutsideDisplayRange(mag, range);
        const p0 = view.canvasCoordsFromPatientCoords(base);
        if (mag < MIN_DISPLACEMENT_MM) {
            out.push({
                x1: p0[0],
                y1: p0[1],
                x2: p0[0],
                y2: p0[1],
                bucket,
                isDot: true,
                isLengthClamped: false,
                isOutsideRange,
            });
            continue;
        }
        const scaledMm = mag * lengthScale;
        const drawnMm = arrowDrawnLengthMm(mag, lengthScale, spacingMm);
        vec3.scaleAndAdd(tip, base, disp, drawnMm / mag);
        const p1 = view.canvasCoordsFromPatientCoords(tip);
        out.push({
            x1: p0[0],
            y1: p0[1],
            x2: p1[0],
            y2: p1[1],
            bucket,
            isDot: false,
            isLengthClamped: scaledMm > drawnMm,
            isOutsideRange,
        });
    }
    return out;
}

function computeEdges(
    view: PlaneView,
    dvf: DvfField,
    lattice: DvfPlaneLattice
): ProjectedEdge[] {
    const { points, uCount, vCount } = lattice;
    const warped: (ReadonlyVec2 | undefined)[] = [];
    const moved = vec3.create();
    for (const base of points) {
        const disp = displacementAt(dvf, base);
        if (!disp) {
            warped.push(undefined);
            continue;
        }
        vec3.add(moved, base, disp);
        warped.push(view.canvasCoordsFromPatientCoords(moved));
    }

    const out: ProjectedEdge[] = [];
    for (const [from, to] of latticeEdgeIndices(uCount, vCount)) {
        const a = warped[from];
        const b = warped[to];
        if (!a || !b) {
            continue;
        }
        out.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1] });
    }
    return out;
}

export default function DvfOverlay({
    view,
    field,
    arrows,
    grid,
    arrowSpacingMm,
    gridSpacingMm,
    rangeLowMm,
    rangeHighMm,
    insideBody,
    basis,
    bounds,
}: DvfOverlayProps) {
    const viewManager = useRecoilValue(viewManagerState);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const planeKeyRef = useRef<string | null>(null);
    const latticeCacheRef = useRef<Map<number, LatticeEntry>>(new Map());
    const tick = usePlaneRedrawTick(viewManager, PLANE_OVERLAY_REDRAW_EVENTS);

    const extent = useMemo(
        () => (field ? (bounds ?? dvfExtent(field)) : undefined),
        [field, bounds]
    );

    const stats = useMemo(() => {
        if (!field || (!arrows && !grid)) {
            return null;
        }
        const range = clampDvfDisplayRange(
            rangeLowMm,
            rangeHighMm,
            dvfMaxMagnitude(field)
        );
        return {
            range,
            // The window's top, so engaging the slider (opened at the field max) keeps lengths.
            lengthScale: arrowLengthScale(range.highMm, arrowSpacingMm),
        };
    }, [arrows, grid, field, arrowSpacingMm, rangeLowMm, rangeHighMm]);

    useEffect(() => {
        const ctx = clearPlaneCanvas(canvasRef.current);
        if (
            !ctx ||
            !view ||
            !field ||
            !extent ||
            !stats ||
            paneImageOf(view).kind !== 'primary'
        ) {
            return;
        }
        const cache = latticeCacheRef.current;
        const planeKey = `${view.label}:${view.planeCoordinates}`;
        if (planeKeyRef.current !== planeKey) {
            cache.clear();
            planeKeyRef.current = planeKey;
        }
        const latticeFor = (pitchMm: number): LatticeEntry => {
            const held = cache.get(pitchMm);
            if (
                held &&
                held.view === view &&
                held.field === field &&
                held.extent === extent &&
                held.insideBody === insideBody &&
                held.basis === basis
            ) {
                return held;
            }
            const built: LatticeEntry = {
                view,
                field,
                extent,
                insideBody,
                basis,
                lattice: planeLattice(view, field, pitchMm, extent),
            };
            cache.set(pitchMm, built);
            return built;
        };
        if (grid) {
            drawEdges(
                ctx,
                computeEdges(view, field, latticeFor(gridSpacingMm).lattice)
            );
        }
        if (arrows) {
            const entry = latticeFor(arrowSpacingMm);
            if (entry.inBody === undefined) {
                entry.inBody = latticeBodyMask(
                    entry.lattice,
                    insideBody,
                    basis
                );
            }
            drawGlyphs(
                ctx,
                computeGlyphs(
                    view,
                    field,
                    entry.lattice,
                    stats.range,
                    stats.lengthScale,
                    arrowSpacingMm,
                    entry.inBody
                )
            );
        }
    }, [
        tick,
        view,
        field,
        extent,
        stats,
        arrowSpacingMm,
        gridSpacingMm,
        arrows,
        grid,
        insideBody,
        basis,
    ]);

    if (!field) {
        return null;
    }
    return (
        <canvas
            ref={canvasRef}
            style={{ ...PLANE_OVERLAY_CANVAS_STYLE, zIndex: 6 }}
            data-testid="deformation-field-overlay"
        />
    );
}
